import { MEDIA } from "@brg/ui"
import { LayoutConst } from "core/modules/layout.module"
import { isServer } from "shared/constants/isServer"
import { convertPxToMm } from "shared/helpers/utils/measureUnits"

export const ASPECT_RATIO = 1.414516129;

export type MediaQueryPropsForCard = {
    xxl?: string
    xl?: string
    lg?: string
    md?: string
    sm?: string
    newBreakpoints?: boolean
}

export type OldBreakpointName = 'xsm' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
export type BreakpointName = 'mobile' | 'desktop' | 'laptop' | 'tablet'

/** Размеры обёртки горизонтальной карточки — как в gallery CardFrame/WrapperVH */
export const wrapperWidthHorizontal = {
    defaultWidth: 564,
    laptop: 432,
    l: 348,
    mobile: 300,
};

export const getHorizontalCardHeight = (width: number) =>
    Number((width / ASPECT_RATIO).toFixed(2));

/** width / height для горизонтальной карточки (CSS aspect-ratio) */
export const HORIZONTAL_CARD_ASPECT_RATIO =
    wrapperWidthHorizontal.defaultWidth / getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth);

export const FIXED_CARD_SIZE_PX: Record<BreakpointName, { w: number; h: number }> = {
    desktop: { w: wrapperWidthHorizontal.defaultWidth, h: getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth) },
    laptop: { w: wrapperWidthHorizontal.defaultWidth, h: getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth) },
    tablet: { w: wrapperWidthHorizontal.l, h: getHorizontalCardHeight(wrapperWidthHorizontal.l) },
    mobile: { w: wrapperWidthHorizontal.mobile, h: getHorizontalCardHeight(wrapperWidthHorizontal.mobile) },
};

export const CARD_FRAME_DW = [82.55, 149.23] as const;
export const CARD_FRAME_DH = [116.77, 105.495] as const;
export const CARD_FRAME_MOBILE_DW_K = 0.2605;
export const CARD_FRAME_MOBILE_DW_C = 0.9;
export const CARD_FRAME_MOBILE_HORIZONTAL_DH_COEF = 0.674;
export const CARD_FRAME_TABLET_COEF = 0.617;

export const FIXED_CARD_LAPTOP_MEDIA = `(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px)`;
export const FIXED_CARD_TABLET_MEDIA = `(max-width: ${LayoutConst.screen.l}px)`;

export const getFixedCardSizeMm = (breakpoint: BreakpointName) => {
    const { w, h } = FIXED_CARD_SIZE_PX[breakpoint];

    return {
        dw: convertPxToMm(w),
        dh: convertPxToMm(h),
        width: w,
        height: h,
    };
};

export type MediaBreakpointName<T extends MediaQueryPropsForCard> = T['newBreakpoints'] extends true ? BreakpointName : OldBreakpointName


export enum DefaultSizeMap {
    xl = '1440px',
    l = '1200px',
    md = '992px',
    sm = '743px', ///768 on constructor
    xsm = '576px',
}

export const MagicCoeff = 3.8

export const dwDescTop = [149.58, 149.47] 
export const dhDescTop = [101.32, 197.37]
export const dwArr = {
    xsm: [72.60, 72.60],
    // xsm: [520 / 3.8, 320 / 3.8],
    sm: [101.10, 100.10],
    md: [118.15, 117.5],
    lg: dwDescTop,
    xl: dwDescTop,
    xxl: dwDescTop,
} 
export const dhArr = {
    xsm: [50.0, 101.0],
    // xsm: [380 / 3.8, 456 / 3.8],
    sm: [66.84, 136.58],
    md: [80, 160],
    lg: [101.32, 197.37],
    xl: dhDescTop,
    xxl: dhDescTop,
}



//Cardconstructor coeff
export const dwDescTopConstructor = [151.58, 149.227]
export const dhDescTopConstructor = [106.84, 209.55]
export const dwArrConstructor = {
    xsm: [151.58, 110.53],
    sm: [151.58, 110.53],
    md: [101.58, 91.227],
    lg: [121.58, 114.076],
    l_920: dwDescTopConstructor,
    xl: [151.58, 114.3],
    xxl: dwDescTopConstructor,
} 
export const dhArrConstructor = {
    xsm: [106.84, 156.58],
    sm: [106.84, 156.58],
    md: dhDescTopConstructor,
    lg: [106.84, 161.55],
    l_920: [88.45, 130],
    xl: [106.84, 157],
    xxl: dhDescTopConstructor,
}
export const bpNameMap = {
    xxl: 'desktop',
    xl: 'desktop',
    l_920: 'laptop',
    lg: 'laptop',
    md: 'tablet',
    sm: 'mobile',
    xsm: 'mobile',
    desktop: 'xxl',
    laptop: 'lg',
    // laptop: 'l_920',
    tablet: 'md',
    mobile: 'sm',
}

export const dwDescTopEditConstructor = [151.579, 149.5]
export const dhDescTopEditConstructor = [106.84, 210]

export const dwArrEditConstructor = {
    xsm: [151.58, 110.53],
    sm: [151.58, 110.52],
    md: dwDescTopEditConstructor,
    lg: dwDescTopEditConstructor,
    l_920: [151.58, 114],
    xl: [151.58, 114],
    xxl: dwDescTopConstructor,
} 
export const dhArrEditConstructor = {
    xsm: [106.84, 156.58],
    sm: [106.84, 156.58],
    md: dhDescTopConstructor,
    lg: dhDescTopConstructor,
    l_920: [106.84, 157],
    xl: [106.84, 157],
    xxl: dhDescTopConstructor,
}

export const queryBreacpoints = { xxl: '1440px', xl: '1200px', lg: '992px', md: '768px', sm: '576px' }

interface MediaQueryProps {
    xxl?: string
    xl?: string
    lg?: string
    md?: string
    sm?: string
    newBreakpoints?: boolean
}

export const getBreakPoint = (props: MediaQueryProps | null = {}) => {
    if (props?.newBreakpoints) {
        if (isServer) {
            return 'mobile'
        }
        if (window.matchMedia(MEDIA.MOBILE).matches) {
            return 'mobile'
        }
        if (window.matchMedia(FIXED_CARD_TABLET_MEDIA).matches) {
            return 'tablet'
        }
        if (window.matchMedia(FIXED_CARD_LAPTOP_MEDIA).matches) {
            return 'laptop'
        }
        return 'desktop'
    }

    const xxlBPoint = props?.xxl;
    const xlBPoint = props?.xl || DefaultSizeMap.xl
    const lgBPoint = props?.lg || DefaultSizeMap.l
    const mdBPoint = props?.md || DefaultSizeMap.md
    const smBPoint = props?.sm || DefaultSizeMap.sm
    if (isServer) {
        return 'xsm'
    }

    if(window.matchMedia(`(max-width: ${smBPoint})`).matches) {        
        return 'xsm'
    } 
    if(window.matchMedia(`(min-width: ${smBPoint}) and (max-width: ${mdBPoint})`).matches) {        
        return 'sm'
    } 
    if(window.matchMedia(`(min-width: ${mdBPoint}) and (max-width: ${lgBPoint})`).matches) {
        return 'md'
    }
    if(window.matchMedia(`(min-width: ${lgBPoint}) and (max-width: ${xlBPoint})`).matches) {
        return 'lg'
    }
    const xlMatchString = xxlBPoint 
        ? `(min-width: ${xlBPoint}) and (max-width: ${xxlBPoint})`
        : `(min-width: ${xlBPoint})`
    if(window.matchMedia(xlMatchString).matches) {
        return 'xl'
    }
    if (window.matchMedia(`(min-width: ${xxlBPoint})`).matches) {
        return 'xxl';
    }
    return 'xsm'
}import { MEDIA } from "@brg/ui"
import { LayoutConst } from "core/modules/layout.module"
import { isServer } from "shared/constants/isServer"
import { convertPxToMm } from "shared/helpers/utils/measureUnits"

export const ASPECT_RATIO = 1.414516129;

export type MediaQueryPropsForCard = {
    xxl?: string
    xl?: string
    lg?: string
    md?: string
    sm?: string
    newBreakpoints?: boolean
}

export type OldBreakpointName = 'xsm' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
export type BreakpointName = 'mobile' | 'desktop' | 'laptop' | 'tablet'

/** Размеры обёртки горизонтальной карточки — как в gallery CardFrame/WrapperVH */
export const wrapperWidthHorizontal = {
    defaultWidth: 564,
    laptop: 432,
    l: 348,
    mobile: 300,
};

export const getHorizontalCardHeight = (width: number) =>
    Number((width / ASPECT_RATIO).toFixed(2));

/** width / height для горизонтальной карточки (CSS aspect-ratio) */
export const HORIZONTAL_CARD_ASPECT_RATIO =
    wrapperWidthHorizontal.defaultWidth / getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth);

export const FIXED_CARD_SIZE_PX: Record<BreakpointName, { w: number; h: number }> = {
    desktop: { w: wrapperWidthHorizontal.defaultWidth, h: getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth) },
    laptop: { w: wrapperWidthHorizontal.defaultWidth, h: getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth) },
    tablet: { w: wrapperWidthHorizontal.l, h: getHorizontalCardHeight(wrapperWidthHorizontal.l) },
    mobile: { w: wrapperWidthHorizontal.mobile, h: getHorizontalCardHeight(wrapperWidthHorizontal.mobile) },
};

export const CARD_FRAME_DW = [82.55, 149.23] as const;
export const CARD_FRAME_DH = [116.77, 105.495] as const;
export const CARD_FRAME_MOBILE_DW_K = 0.2605;
export const CARD_FRAME_MOBILE_DW_C = 0.9;
export const CARD_FRAME_MOBILE_HORIZONTAL_DH_COEF = 0.674;
export const CARD_FRAME_TABLET_COEF = 0.617;

export const FIXED_CARD_LAPTOP_MEDIA = `(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px)`;
export const FIXED_CARD_TABLET_MEDIA = `(max-width: ${LayoutConst.screen.l}px)`;

export const getFixedCardSizeMm = (breakpoint: BreakpointName) => {
    const { w, h } = FIXED_CARD_SIZE_PX[breakpoint];

    return {
        dw: convertPxToMm(w),
        dh: convertPxToMm(h),
        width: w,
        height: h,
    };
};

export type MediaBreakpointName<T extends MediaQueryPropsForCard> = T['newBreakpoints'] extends true ? BreakpointName : OldBreakpointName


export enum DefaultSizeMap {
    xl = '1440px',
    l = '1200px',
    md = '992px',
    sm = '743px', ///768 on constructor
    xsm = '576px',
}

export const MagicCoeff = 3.8

export const dwDescTop = [149.58, 149.47] 
export const dhDescTop = [101.32, 197.37]
export const dwArr = {
    xsm: [72.60, 72.60],
    // xsm: [520 / 3.8, 320 / 3.8],
    sm: [101.10, 100.10],
    md: [118.15, 117.5],
    lg: dwDescTop,
    xl: dwDescTop,
    xxl: dwDescTop,
} 
export const dhArr = {
    xsm: [50.0, 101.0],
    // xsm: [380 / 3.8, 456 / 3.8],
    sm: [66.84, 136.58],
    md: [80, 160],
    lg: [101.32, 197.37],
    xl: dhDescTop,
    xxl: dhDescTop,
}



//Cardconstructor coeff
export const dwDescTopConstructor = [151.58, 149.227]
export const dhDescTopConstructor = [106.84, 209.55]
export const dwArrConstructor = {
    xsm: [151.58, 110.53],
    sm: [151.58, 110.53],
    md: [101.58, 91.227],
    lg: [121.58, 114.076],
    l_920: dwDescTopConstructor,
    xl: [151.58, 114.3],
    xxl: dwDescTopConstructor,
} 
export const dhArrConstructor = {
    xsm: [106.84, 156.58],
    sm: [106.84, 156.58],
    md: dhDescTopConstructor,
    lg: [106.84, 161.55],
    l_920: [88.45, 130],
    xl: [106.84, 157],
    xxl: dhDescTopConstructor,
}
export const bpNameMap = {
    xxl: 'desktop',
    xl: 'desktop',
    l_920: 'laptop',
    lg: 'laptop',
    md: 'tablet',
    sm: 'mobile',
    xsm: 'mobile',
    desktop: 'xxl',
    laptop: 'lg',
    // laptop: 'l_920',
    tablet: 'md',
    mobile: 'sm',
}

export const dwDescTopEditConstructor = [151.579, 149.5]
export const dhDescTopEditConstructor = [106.84, 210]

export const dwArrEditConstructor = {
    xsm: [151.58, 110.53],
    sm: [151.58, 110.52],
    md: dwDescTopEditConstructor,
    lg: dwDescTopEditConstructor,
    l_920: [151.58, 114],
    xl: [151.58, 114],
    xxl: dwDescTopConstructor,
} 
export const dhArrEditConstructor = {
    xsm: [106.84, 156.58],
    sm: [106.84, 156.58],
    md: dhDescTopConstructor,
    lg: dhDescTopConstructor,
    l_920: [106.84, 157],
    xl: [106.84, 157],
    xxl: dhDescTopConstructor,
}

export const queryBreacpoints = { xxl: '1440px', xl: '1200px', lg: '992px', md: '768px', sm: '576px' }

interface MediaQueryProps {
    xxl?: string
    xl?: string
    lg?: string
    md?: string
    sm?: string
    newBreakpoints?: boolean
}

export const getBreakPoint = (props: MediaQueryProps | null = {}) => {
    if (props?.newBreakpoints) {
        if (isServer) {
            return 'mobile'
        }
        if (window.matchMedia(MEDIA.MOBILE).matches) {
            return 'mobile'
        }
        if (window.matchMedia(FIXED_CARD_TABLET_MEDIA).matches) {
            return 'tablet'
        }
        if (window.matchMedia(FIXED_CARD_LAPTOP_MEDIA).matches) {
            return 'laptop'
        }
        return 'desktop'
    }

    const xxlBPoint = props?.xxl;
    const xlBPoint = props?.xl || DefaultSizeMap.xl
    const lgBPoint = props?.lg || DefaultSizeMap.l
    const mdBPoint = props?.md || DefaultSizeMap.md
    const smBPoint = props?.sm || DefaultSizeMap.sm
    if (isServer) {
        return 'xsm'
    }

    if(window.matchMedia(`(max-width: ${smBPoint})`).matches) {        
        return 'xsm'
    } 
    if(window.matchMedia(`(min-width: ${smBPoint}) and (max-width: ${mdBPoint})`).matches) {        
        return 'sm'
    } 
    if(window.matchMedia(`(min-width: ${mdBPoint}) and (max-width: ${lgBPoint})`).matches) {
        return 'md'
    }
    if(window.matchMedia(`(min-width: ${lgBPoint}) and (max-width: ${xlBPoint})`).matches) {
        return 'lg'
    }
    const xlMatchString = xxlBPoint 
        ? `(min-width: ${xlBPoint}) and (max-width: ${xxlBPoint})`
        : `(min-width: ${xlBPoint})`
    if(window.matchMedia(xlMatchString).matches) {
        return 'xl'
    }
    if (window.matchMedia(`(min-width: ${xxlBPoint})`).matches) {
        return 'xxl';
    }
    return 'xsm'
}